import api from './api';
import { downloadResponse } from '../utils/downloadResponse';
import { ApiResponse } from '../types';
import {
  ApprovalRule, BankAccountReconciliation, CashCount, CashCountInput, CashCountStatus, CloseChecklistItem, OpeningBalances, Reconciliation, YearEndStatus,
  ChartResponse, FinanceSettings, FiscalPeriod, FiscalPeriodAction, JournalEntryDetail, JournalFilters,
  JournalListResponse, LedgerAccount, ManualJournal, ManualJournalInput, ManualJournalListResponse,
  ManualJournalStatus, TrialBalance, AccountLedgerFilters, AccountLedgerReport, AccountLedgerResponse, LedgerControls, CashBankFilters, CashBankReport, Statement, StatementKind, StatementQuery, CashBankResponse, FeeRequest, FeeRequestKind, FeeRequestLimits, FeeRequestStatus,
} from '../types/finance';
import { Pagination } from '../types';

const unwrap = <T,>(r: { data: ApiResponse<T> }): T => {
  if (!r.data.success) throw new Error(r.data.message);
  return r.data.data as T;
};

const query = (params: Record<string, string | number | null | undefined>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') search.append(k, String(v)); });
  const s = search.toString();
  return s ? `?${s}` : '';
};

const financeService = {
  // Chart & preferences
  getChart: async () => unwrap<ChartResponse>(await api.get('/finance/accounts')),
  createAccount: async (input: { parentCode: string; code: string; name: string; description?: string | null }) =>
    unwrap<LedgerAccount>(await api.post('/finance/accounts', input)),
  updateAccount: async (id: string, input: { name?: string; description?: string | null; active?: boolean }) =>
    unwrap<LedgerAccount>(await api.patch(`/finance/accounts/${id}`, input)),
  getSettings: async () => unwrap<FinanceSettings>(await api.get('/finance/settings')),
  getApprovalRule: async () => unwrap<ApprovalRule>(await api.get('/finance/approval-rule')),
  updateSettings: async (input: Partial<FinanceSettings>) => unwrap<FinanceSettings>(await api.put('/finance/settings', input)),

  // Fiscal periods
  getFiscalYears: async () => unwrap<{ current: number; years: number[] }>(await api.get('/finance/periods/years')),
  getPeriods: async (fiscalYear: number) => unwrap<FiscalPeriod[]>(await api.get(`/finance/periods${query({ fiscalYear })}`)),
  changePeriod: async (id: string, action: FiscalPeriodAction, reason?: string) =>
    unwrap<FiscalPeriod>(await api.post(`/finance/periods/${id}/${action}`, { reason })),
  getCloseChecklist: async (id: string) => unwrap<CloseChecklistItem[]>(await api.get(`/finance/periods/${id}/checklist`)),
  getYearEnd: async (fiscalYear: number) => unwrap<YearEndStatus>(await api.get(`/finance/years/${fiscalYear}/close`)),
  closeYear: async (fiscalYear: number) => unwrap<YearEndStatus>(await api.post(`/finance/years/${fiscalYear}/close`)),

  // Opening balances
  getOpeningBalances: async () => unwrap<OpeningBalances>(await api.get('/finance/opening-balances')),
  saveOpeningBalances: async (input: { date: string; balances: Array<{ accountId: string; balancePaisa: number }> }) =>
    unwrap<{ entryNo: string } | null>(await api.put('/finance/opening-balances', input)),

  // Trial balance & journal
  getStatement: async (kind: StatementKind, q: StatementQuery) =>
    unwrap<Statement>(await api.get(`/finance/statements/${kind}${query({ ...q })}`)),
  // Finance PDFs are drawn in the browser from the screens' data; the CSVs come from the server.
  exportStatementCsv: async (kind: StatementKind, q: StatementQuery) =>
    downloadResponse(await api.get(`/finance/statements/${kind}/export/csv${query({ ...q })}`, { responseType: 'blob' }), `${kind}.csv`),
  getTrialBalance: async (asOf?: string) => unwrap<TrialBalance>(await api.get(`/finance/trial-balance${query({ asOf })}`)),
  listJournal: async (f: JournalFilters) => unwrap<JournalListResponse>(await api.get(`/finance/journal${query({ ...f })}`)),
  getJournalEntry: async (id: string) => unwrap<JournalEntryDetail>(await api.get(`/finance/journal/${id}`)),
  reverseJournalEntry: async (id: string, reason: string) =>
    unwrap<{ entryNo: string }>(await api.post(`/finance/journal/${id}/reverse`, { reason })),

  // Account ledger
  getAccountLedger: async (accountId: string, f: AccountLedgerFilters) =>
    unwrap<AccountLedgerResponse>(await api.get(`/finance/ledger/${accountId}${query({ ...f })}`)),
  exportAccountLedgerCsv: async (accountId: string, f: AccountLedgerFilters) =>
    downloadResponse(await api.get(`/finance/ledger/${accountId}/export/csv${query({ ...f, page: undefined, limit: undefined })}`, { responseType: 'blob' }),
      'ledger.csv'),
  getAccountLedgerReport: async (accountId: string, f: AccountLedgerFilters) =>
    unwrap<AccountLedgerReport>(await api.get(`/finance/ledger/${accountId}/export/report${query({ ...f, page: undefined, limit: undefined })}`)),

  // Control checks
  getControls: async () => unwrap<LedgerControls>(await api.get('/finance/controls')),
  getCashBank: async (f: CashBankFilters) => unwrap<CashBankResponse>(await api.get(`/finance/cash-bank${query({ ...f })}`)),
  exportCashBankCsv: async (f: CashBankFilters) =>
    downloadResponse(await api.get(`/finance/cash-bank/export/csv${query({ ...f, page: undefined, limit: undefined })}`, { responseType: 'blob' }),
      'cash-and-bank.csv'),
  getCashBankReport: async (f: CashBankFilters) =>
    unwrap<CashBankReport>(await api.get(`/finance/cash-bank/export/report${query({ ...f, page: undefined, limit: undefined })}`)),

  // Cash counts (the cashier's day close)
  listCashCounts: async (f: { status?: CashCountStatus; accountId?: string; page?: number; limit?: number }) =>
    unwrap<{ items: CashCount[]; pagination: Pagination }>(await api.get(`/finance/cash-counts${query({ ...f })}`)),
  getExpectedCash: async (accountId: string, day: string) =>
    unwrap<{ expectedPaisa: number }>(await api.get(`/finance/cash-counts/expected${query({ accountId, day })}`)),
  recordCashCount: async (input: CashCountInput) => unwrap<CashCount>(await api.post('/finance/cash-counts', input)),
  approveCashCount: async (id: string) => unwrap<{ entryNo: string }>(await api.post(`/finance/cash-counts/${id}/approve`)),
  rejectCashCount: async (id: string, reason: string) => unwrap<CashCount>(await api.post(`/finance/cash-counts/${id}/reject`, { reason })),

  // Bank reconciliation
  getReconciliations: async () => unwrap<BankAccountReconciliation[]>(await api.get('/finance/reconciliations')),
  startReconciliation: async (input: { accountId: string; statementDate: string; statementBalancePaisa: number }) =>
    unwrap<{ _id: string }>(await api.post('/finance/reconciliations', input)),
  getReconciliation: async (id: string) => unwrap<Reconciliation>(await api.get(`/finance/reconciliations/${id}`)),
  updateReconciliation: async (id: string, input: { statementDate?: string; statementBalancePaisa?: number }) =>
    unwrap<{ _id: string }>(await api.patch(`/finance/reconciliations/${id}`, input)),
  discardReconciliation: async (id: string) => unwrap<null>(await api.delete(`/finance/reconciliations/${id}`)),
  setCleared: async (id: string, lineIds: string[], cleared: boolean) =>
    unwrap<{ _id: string }>(await api.post(`/finance/reconciliations/${id}/cleared`, { lineIds, cleared })),
  addBankAdjustment: async (id: string, input: { kind: 'charge' | 'profit'; amountPaisa: number; date?: string; note: string }) =>
    unwrap<{ entryNo: string }>(await api.post(`/finance/reconciliations/${id}/adjustments`, input)),
  completeReconciliation: async (id: string) => unwrap<{ _id: string }>(await api.post(`/finance/reconciliations/${id}/complete`)),
  exportReconciliationCsv: async (id: string) =>
    downloadResponse(await api.get(`/finance/reconciliations/${id}/export/csv`, { responseType: 'blob' }), 'bank-reconciliation.csv'),

  // Write-offs, refunds and deposit returns
  listFeeRequests: async (f: { status?: FeeRequestStatus; invoiceId?: string; page?: number; limit?: number }) =>
    unwrap<{ items: FeeRequest[]; pagination: Pagination }>(await api.get(`/fee-requests${query({ ...f })}`)),
  getFeeRequestLimits: async (invoiceId: string) => unwrap<FeeRequestLimits>(await api.get(`/fee-requests/limits/${invoiceId}`)),
  createFeeRequest: async (input: { kind: FeeRequestKind; invoiceId: string; amount: number; method?: string | null; reference?: string; reason: string }) =>
    unwrap<FeeRequest>(await api.post('/fee-requests', input)),
  approveFeeRequest: async (id: string) => unwrap<FeeRequest>(await api.post(`/fee-requests/${id}/approve`)),
  rejectFeeRequest: async (id: string, reason: string) => unwrap<FeeRequest>(await api.post(`/fee-requests/${id}/reject`, { reason })),

  // Manual journals
  listManualJournals: async (status?: ManualJournalStatus, page = 1, limit = 20) =>
    unwrap<ManualJournalListResponse>(await api.get(`/finance/manual-journals${query({ status, page, limit })}`)),
  createManualJournal: async (input: ManualJournalInput) =>
    unwrap<{ journal: ManualJournal; entry: { entryNo: string } | null }>(await api.post('/finance/manual-journals', input)),
  approveManualJournal: async (id: string) => unwrap<{ entryNo: string }>(await api.post(`/finance/manual-journals/${id}/approve`)),
  rejectManualJournal: async (id: string, reason: string) =>
    unwrap<ManualJournal>(await api.post(`/finance/manual-journals/${id}/reject`, { reason })),
};

export default financeService;
